import { useContext, useMemo } from 'react';
import SupportThreads from '../display/SupportThreads';
import { useKillTeamsContext } from '../../contexts/KillTeamsContext';
import { squadIdsOf } from '../../engine/killTeam/marks';
import BattlefieldTokens from '../BattlefieldTokens';
import { DisplayOccluderContext, DisplayRiseContext, DisplayVeilContext } from '../display/displayFaces';
import { DISPLAY_FIGURE } from '../display/figureStyle';
import { useDisplayOcclusion } from '../../hooks/useDisplayOcclusion';
import TargetChevron from '../display/TargetChevron';
import DisplayBeats from '../display/DisplayBeats';
import SquadThread from '../display/SquadThread';
import ContactMarks from '../display/ContactMarks';
import GangUpThreads from '../display/GangUpThreads';
import CloseInGhost from '../display/CloseInGhost';
import CastRings from '../display/CastRings';
import useCastChange from '../../hooks/useCastChange';
import { gangUpOf } from '../../engine/display/gangUp';
import SightWedges from '../display/SightWedges';
import { liveryByCombatant } from '../../engine/roster/team';
import useSquadPreview from '../../hooks/useSquadPreview';
import useOnsets from '../../hooks/useOnsets';
import { useFalls } from '../../hooks/useDisplayBeats';
import useHoldsStill from '../../hooks/useHoldsStill';
import { EMPTY, EMPTY_REACH, EMPTY_RECORD } from './panelDefaults';
import {
  overlapNotesFor,
  conditionNotesFor,
  surfaceNotesFor,
  joinMarkerNotes,
  markerStatusesFor,
  bodyTypesFor,
  tokenScalesFor,
  crowdsFor,
  figureModelsFor,
  headingsOf,
  statesFor,
  posturesOf,
  vehicleFacingsFor,
  hordesFor,
  objectsFor,
  traitGroundsFor,
  wallSidesOf,
  surfaceStepsFor,
  surfaceEntriesOf,
  otherSideOf,
  inTheDarkFor,
} from './markerDecorations';
import { noiseHeard } from '../../engine/stealth/alerts';
import { surfacesOf } from '../../engine/display/surfaces';

/** How long a ghost's ripple at a wall lasts (the traits page: 700 ms), and a step into a surface's ring. */
const RIPPLE_MS = 700;
const ENTRY_MS = 700;

/** Stealth's map (Z2): the noise the wedges turn to, and who stands in the dark (dimmed). */
function useStealthMarks(display, panel, map) {
  const { beats, drawnTokenList } = map;
  return {
    heard: useMemo(
      () => (display && panel.inStealth ? noiseHeard(beats ?? [], panel.signatureReach) : null),
      [display, panel.inStealth, panel.signatureReach, beats],
    ),
    dark: useMemo(
      () => (display && panel.terrain ? inTheDarkFor(drawnTokenList, panel.terrain) : EMPTY_RECORD),
      [display, drawnTokenList, panel.terrain],
    ),
  };
}

/** Everything a marker wears, memoized on what each piece reads. */
function useMarkerDecorations(panel, map) {
  const { roster, tokens, ignoreTerrain, isGm, applyConditionModifiers, fatigueSlowsMovement, surfaceEffects } = panel;
  const { tokenList, solids, rulesFor, tooBig } = map;
  const level = map.prefs.markerStatus;
  const display = map.renderer === 'display';
  const overlaps = useMemo(
    () => overlapNotesFor(tokenList, solids, { ignoreTerrain, isGm, rulesFor, tooBig }),
    [tokenList, solids, ignoreTerrain, isGm, rulesFor, tooBig],
  );
  const conditionNotes = useMemo(
    () => conditionNotesFor(roster, tokens, { applyConditionModifiers, fatigueSlowsMovement }),
    [roster, tokens, applyConditionModifiers, fatigueSlowsMovement],
  );
  const surfaceNotes = useMemo(() => surfaceNotesFor(surfaceEffects), [surfaceEffects]);
  const { actorId, actorAmmo } = panel;
  const { maxChips } = map.display;
  // Only the display draws figures by their model and their looks; the classic map never pays for either.
  const models = useMemo(() => (display ? figureModelsFor(roster, tokens) : EMPTY), [display, roster, tokens]);
  return {
    // Whose side everyone is on. The body wears it; the role (actor,
    // target) stays a stroke and a mark (v8.313.0).
    liveries: useMemo(() => liveryByCombatant(roster), [roster]),
    // Who is in Squad Mode: their name pill wears the linked chevrons (2026-10-23).
    inSquad: useMemo(() => squadIdsOf(roster), [roster]),
    support: useSupport(display, roster, panel),
    notes: useMemo(
      () => joinMarkerNotes(overlaps, conditionNotes, surfaceNotes),
      [overlaps, conditionNotes, surfaceNotes],
    ),
    status: useMemo(
      () => markerStatusesFor(roster, tokens, { level, actorId, actorAmmo, maxChips }),
      [roster, tokens, level, actorId, actorAmmo, maxChips],
    ),
    bodyTypes: useMemo(() => bodyTypesFor(roster, tokens), [roster, tokens]),
    scales: useMemo(() => tokenScalesFor(roster, tokens), [roster, tokens]),
    headings: useMemo(() => headingsOf(tokenList), [tokenList]),
    crowds: useMemo(() => crowdsFor(roster, tokens), [roster, tokens]),
    models,
    ...useStealthMarks(display, panel, map),
    // Who of the actor's team stands in reach of the target beside them (Z1), for the gang-up threads.
    gang: useMemo(
      () => (display ? gangUpOf(roster, map.drawnTokens, panel.actorId ?? null, panel.targetId ?? null) : null),
      [display, roster, map.drawnTokens, panel.actorId, panel.targetId],
    ),
    // The heat under the other side (T4), when the viewer has it on.
    heat: useMemo(
      () => (display && map.display.heat ? otherSideOf(roster, map.display.actorId ?? actorId) : EMPTY_RECORD),
      [display, map.display.heat, map.display.actorId, roster, actorId],
    ),
    ...useFigureMotion(display, panel, models, map),
    // The display's hordes, a body per point (V3): the classic map keeps its three.
    hordes: useMemo(() => (display ? hordesFor(roster, tokens) : EMPTY_RECORD), [display, roster, tokens]),
    // The display's objects (2026-10-26): the ring that is each one's gauge, and its shards once destroyed.
    objects: useMemo(() => (display ? objectsFor(roster, tokens) : EMPTY_RECORD), [display, roster, tokens]),
    // The display's facing rings (V1): the classic map keeps its heading arrow alone.
    vehicleFacings: useMemo(
      () => (display ? vehicleFacingsFor(roster, tokens, actorId, panel.vehicleArcPreset) : EMPTY_RECORD),
      [display, roster, tokens, actorId, panel.vehicleArcPreset],
    ),
  };
}

/**
 * What the display's figures wear and do over time (plan design-gaps, S2-S4): their standing states with each
 * onset, the posture's onset (a figure topples as it falls, turns back up as it stands), and which way each fell,
 * as this screen saw the blow - beside the models, so a revival drops it. The classic map gets empty records, so the
 * markers need not ask which renderer they are on.
 */
function useFigureMotion(display, { roster, tokens, isGm }, models, map) {
  const states = useMemo(() => (display ? statesFor(roster, tokens) : EMPTY_RECORD), [display, roster, tokens]);
  const still = useHoldsStill();
  const { drawnTokenList, solids, groundAt } = map;
  // What each figure's trait reads of the map (T1): the ground under it, and a ghost's side of the wall.
  const traitGrounds = useMemo(
    () => (display ? traitGroundsFor(drawnTokenList, models, solids, groundAt) : EMPTY_RECORD),
    [display, drawnTokenList, models, solids, groundAt],
  );
  return {
    traitGrounds,
    surfaceEntries: useSurfaceEntries(display, map, isGm, models, still),
    rippleOnsets: useOnsets(
      useMemo(() => wallSidesOf(traitGrounds), [traitGrounds]),
      still,
      RIPPLE_MS,
    ),
    states,
    still,
    stateOnsets: useOnsets(states, still),
    postureOnsets: useOnsets(
      useMemo(() => posturesOf(models), [models]),
      still,
    ),
    falls: useFalls(map.beats, models, tokens),
  };
}

/**
 * A cut's cast change on this screen (Z3: hooks/useCastChange), and the posture onsets with the newcomers standing
 * up in them - the figure's own Stand, from the floor.
 */
function useCutCast(display, panel, map, d) {
  const rise = useContext(DisplayRiseContext);
  const { stage } = panel;
  // The committed figures, not the drawn ones: those change every frame of a walk (the Track Z review).
  const cast = useCastChange(
    display ? map.tokenList : EMPTY,
    stage?.cutAt ?? null,
    display ? rise : 0,
    stage?.travellers,
    d.still,
  );
  const postureOnsets = useMemo(
    () =>
      cast.entering.length
        ? { ...d.postureOnsets, ...Object.fromEntries(cast.entering.map((id) => [id, ['stand']])) }
        : d.postureOnsets,
    [cast.entering, d.postureOnsets],
  );
  return { cast, postureOnsets };
}

/**
 * The figures stepping into a surface this moment (T3: the entry beat), by token id, with the surface's colour: what
 * each drawn figure stands in, its onsets, and those that are a surface rather than bare ground.
 */
function useSurfaceEntries(display, map, isGm, models, still) {
  const surfaces = useMemo(() => (display ? surfacesOf(map.solids, isGm) : EMPTY), [display, map.solids, isGm]);
  const steps = useMemo(
    () => (display && surfaces.length ? surfaceStepsFor(map.drawnTokenList, models, surfaces) : EMPTY_RECORD),
    [display, surfaces, map.drawnTokenList, models],
  );
  const onsets = useOnsets(steps, still, ENTRY_MS);
  return useMemo(() => surfaceEntriesOf(onsets, steps), [onsets, steps]);
}

/**
 * The acting brother's support ring and threads (Squad Mode), on the display only, over the roster the players share:
 * a hidden figure is never threaded. Who is in range, and the metres, come from the RULE markers through the context
 * (positions off: nothing drawn, as no rule reads the map); the lines are drawn at the drawn markers.
 */
function useSupport(display, roster, panel) {
  const k = useKillTeamsContext();
  const teams = k?.teams;
  const support = k?.support;
  const hidden = panel.hiddenIds;
  // Enter or Challenge aimed at (the strip's button, the drum's cursor): a Solo brother sees what he would have.
  const preview = useSquadPreview();
  return useMemo(() => {
    if (!display || !teams || !support) return null;
    const shared = hidden?.size ? roster.filter((c) => !hidden.has(c.id)) : roster;
    return support(panel.actorId ?? null, shared, preview);
  }, [display, teams, support, hidden, roster, panel.actorId, preview]);
}

/**
 * The markers. Not draggable while building or deploying: in Deploy a
 * marker that takes the press swallows the click meant to PLANT the flag,
 * and where the auto-placed combatants happen to stand decides whether it
 * happens - a bug that looks exactly like flakiness. Hit-testable in
 * Deploy though, which is what right-click-to-recall needs; Build stays
 * inert.
 */
/**
 * The display's marks under the figures: the controller's thread to its units (plan map-display, E), where the party
 * last saw what it cannot see (H: "?" marks and their trails), the GM's proposed close-in (Z2, the director's map
 * alone), a cut's cast change (Z3), the gang the actor may join (Z1), and what the
 * opposition sees (H, Z2: wedges, turned to a noise, and the leader's sweep).
 */
function DisplayUnderlays({ panel, map, d }) {
  const { camera, drawnTokens } = map;
  return (
    <>
      <SquadThread roster={panel.roster} tokens={drawnTokens} actorId={panel.actorId} camera={camera} />
      <SupportThreads view={d.support} livery={d.liveries[panel.actorId]} tokens={drawnTokens} camera={camera} />
      <ContactMarks contacts={panel.contactMarks} camera={camera} />
      <CloseInGhost proposal={panel.closeIn} tokens={drawnTokens} camera={camera} />
      <CastRings cast={d.cast} tokens={drawnTokens} camera={camera} />
      <GangUpThreads
        gang={d.gang}
        tokens={drawnTokens}
        targetId={panel.targetId}
        pressed={!!panel.gangUp}
        camera={camera}
      />
      <SightWedges
        watchers={panel.sightWatchers}
        tokens={drawnTokens}
        rung={panel.stealthRung}
        camera={camera}
        heard={d.heard}
        leaderId={panel.stealthLeader}
      />
    </>
  );
}

export default function PanelMarkers({ panel, map }) {
  const decorations = useMarkerDecorations(panel, map);
  const d = { ...decorations, ...useCutCast(map.renderer === 'display', panel, map, decorations) };
  const { building, deploying } = map;
  // On the display, figures wear its look and turn see-through behind terrain (plan map-display, A7).
  const occluded = useDisplayOcclusion(map.drawnTokenList, useContext(DisplayOccluderContext));
  // Behind translucent terrain a figure is half seen (T3): the same test, against the veils.
  const halfSeen = useDisplayOcclusion(map.drawnTokenList, useContext(DisplayVeilContext));
  // What the display shows of the action (quiet map, pills, numbers, chevron): useDisplayAction's.
  const { display } = map;
  const hovered = map.dropOverId ?? panel.hoveredId;
  return (
    <>
      {map.renderer === 'display' && <DisplayUnderlays panel={panel} map={map} d={d} />}
      <BattlefieldTokens
        figure={map.renderer === 'display' ? DISPLAY_FIGURE : undefined}
        occluded={occluded}
        halfSeen={halfSeen}
        tokens={map.drawnTokenList}
        walks={map.walksPlaying}
        shoves={map.shoves}
        camera={map.camera}
        actorId={panel.actorId}
        targetId={panel.targetId}
        names={map.names}
        liveries={d.liveries}
        inSquad={d.inSquad}
        hoveredId={hovered}
        onHoverToken={panel.onHoverToken}
        reach={building || deploying ? EMPTY_REACH : panel.reachMarks}
        impacts={panel.impacts}
        hidden={panel.hiddenIds}
        draggable={!building && !deploying}
        pickable={!building}
        showLabels={map.prefs.showLabels}
        bodyTypes={d.bodyTypes}
        scales={d.scales}
        crowds={d.crowds}
        models={d.models}
        looks={map.looks}
        states={d.states}
        stateOnsets={d.stateOnsets}
        postureOnsets={d.postureOnsets}
        falls={d.falls}
        vehicleFacings={d.vehicleFacings}
        hordes={d.hordes}
        objects={d.objects}
        still={d.still}
        traitGrounds={d.traitGrounds}
        rippleOnsets={d.rippleOnsets}
        surfaceEntries={d.surfaceEntries}
        heat={d.heat}
        dark={d.dark}
        alerts={panel.oppositionAlerts}
        headings={d.headings}
        canTurn={map.canTurn}
        coverScreens={map.cover?.screens}
        coverRiseKeys={map.cover?.riseKeys}
        onHeadingPointerDown={map.onHeadingPointerDown}
        overlaps={d.notes}
        status={d.status}
        onTokenPointerDown={map.onTokenPointerDown}
        focus={display.focus}
        pills={display.pills}
        numbersOff={display.numbersOff}
      />
      <TargetChevron target={display.chevronTarget} camera={map.camera} />
      {/* The attacks as the display plays them (plan map-display, D); the classic map keeps its marker flash. */}
      {map.renderer === 'display' && (
        <DisplayBeats
          beats={map.beats}
          camera={map.camera}
          inStealth={!!panel.inStealth}
          reach={panel.signatureReach}
        />
      )}
    </>
  );
}
